import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, Cell, ReferenceLine,
} from 'recharts';
import api from '../../services/api';
import useDefaultTradingMode from '../../hooks/useDefaultTradingMode';
import {
  CHAIN_LABELS, STRATEGY_LABELS, formatNum, getNativeSymbol,
} from '../../utils/masterChefUtils';

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: '7 Days' },
  { id: '30d', label: '30 Days' },
  { id: 'all', label: 'All Time' },
];

const MODES = [
  { id: '', label: 'All' },
  { id: 'live', label: 'Live' },
  { id: 'dry_run', label: 'Dry Run' },
];

const CHAIN_COLORS = {
  solana: '#a855f7',
  base: '#3b82f6',
  ethereum: '#6366f1',
  bsc: '#eab308',
};

function KpiCard({ label, value, sub, valueClass = 'text-gray-900 dark:text-white' }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
      <p className={`mt-1 text-xl font-bold ${valueClass}`}>{value}</p>
      {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function PnlTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg border border-gray-700">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color }}>
          {entry.name}: {entry.value >= 0 ? '+' : ''}{formatNum(entry.value, 4)}
        </p>
      ))}
    </div>
  );
}

function DailyTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  return (
    <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg border border-gray-700">
      <p className="font-medium mb-1">{label}</p>
      {row && (
        <>
          <p className="text-gray-300">Opened: {row.opened} · Closed: {row.closed}</p>
          <p className="text-gray-300">W/L: {row.wins}/{row.losses}</p>
        </>
      )}
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color }}>
          {entry.name}: {entry.value >= 0 ? '+' : ''}{formatNum(entry.value, 4)}
        </p>
      ))}
    </div>
  );
}

export default function PortfolioPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [mode, setMode] = useDefaultTradingMode('');
  const [chainFilter, setChainFilter] = useState('');

  const fetchPortfolio = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (mode) params.set('mode', mode);
      const res = await api.get(`/masterchef/portfolio?${params}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch portfolio:', err);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    setLoading(true);
    fetchPortfolio();
    const interval = setInterval(fetchPortfolio, 30000);
    return () => clearInterval(interval);
  }, [fetchPortfolio]);

  const periodStats = data?.periods?.[period];
  const chainStats = data?.periodsByChain?.[period] || {};
  // Show solana first, then the rest in a stable preferred order
  const CHAIN_ORDER = { solana: 0, ethereum: 1, base: 2, bsc: 3 };
  const chains = useMemo(
    () => [...(data?.chains || [])].sort(
      (a, b) => (CHAIN_ORDER[a] ?? 99) - (CHAIN_ORDER[b] ?? 99) || a.localeCompare(b)
    ),
    [data?.chains]
  );
  const activeChains = chainFilter ? [chainFilter] : chains;

  const dailyChartData = useMemo(() => {
    if (!data?.daily) return [];
    const slice = period === '7d' ? data.daily.slice(-7)
      : period === '30d' ? data.daily
        : period === 'today' ? data.daily.slice(-1)
          : period === 'yesterday' ? data.daily.slice(-2, -1)
            : data.daily;
    return slice.map((d) => {
      const row = { ...d, label: d.label };
      for (const c of activeChains) {
        row[c] = d.pnl?.[c] || 0;
      }
      return row;
    });
  }, [data?.daily, period, activeChains]);

  const cumulativeChartData = useMemo(() => {
    if (!data?.cumulative?.length) return [];
    const running = {};
    return data.cumulative.map((pt, i) => {
      for (const c of activeChains) {
        running[c] = pt.cumulative?.[c] ?? running[c] ?? 0;
      }
      const row = {
        index: i + 1,
        date: pt.date,
        symbol: pt.symbol,
        label: pt.date?.slice(5) || '',
      };
      for (const c of activeChains) row[c] = running[c];
      return row;
    });
  }, [data?.cumulative, activeChains]);

  const strategyData = useMemo(() => {
    const raw = data?.strategyByPeriod?.[period] || {};
    return Object.entries(raw)
      .map(([key, s]) => ({
        name: STRATEGY_LABELS[key] || key,
        closed: s.closed || 0,
        winRate: s.winRate || 0,
        pnl: s.totalPnl || 0,
      }))
      .filter((r) => r.closed > 0)
      .sort((a, b) => b.closed - a.closed);
  }, [data?.strategyByPeriod, period]);

  const recentTrades = data?.recentByPeriod?.[period] || [];

  const singleChain = activeChains.length === 1 ? activeChains[0] : null;
  const singleSym = singleChain ? getNativeSymbol(singleChain) : null;

  const displayPnl = singleChain
    ? (chainStats[singleChain]?.realizedPnl ?? 0)
    : null;
  const displayUnrealized = singleChain
    ? (chainStats[singleChain]?.unrealizedPnl ?? 0)
    : null;

  if (loading && !data) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-sm text-gray-500">
        Loading portfolio analytics...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header + filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base font-bold text-gray-900 dark:text-white">Portfolio Overview</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Realized P&L by close date · per-chain native units</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {MODES.map((m) => (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  mode === m.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {m.label}
              </button>
            ))}
            <select
              value={chainFilter}
              onChange={(e) => setChainFilter(e.target.value)}
              className="text-xs px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All chains</option>
              {chains.map((c) => (
                <option key={c} value={c}>{CHAIN_LABELS[c] || c}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Period tabs */}
        <div className="flex flex-wrap gap-1 p-1 bg-gray-100 dark:bg-gray-900/50 rounded-lg">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`flex-1 min-w-[72px] px-3 py-2 text-xs font-semibold rounded-md transition-colors ${
                period === p.id
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label="Opened" value={periodStats?.opened ?? 0} sub={`${periodStats?.active ?? 0} still open`} />
        <KpiCard label="Closed" value={periodStats?.closed ?? 0} sub={`${periodStats?.wins ?? 0}W / ${periodStats?.losses ?? 0}L`} />
        <KpiCard
          label="Win Rate"
          value={`${periodStats?.winRate ?? 0}%`}
          valueClass={(periodStats?.winRate ?? 0) >= 50 ? 'text-green-600' : 'text-orange-600'}
        />
        <KpiCard
          label="Realized P&L"
          value={singleChain
            ? `${(displayPnl ?? 0) >= 0 ? '+' : ''}${formatNum(displayPnl, 3)} ${singleSym}`
            : '—'}
          sub={singleChain ? `${periodStats?.realizedPnlPercent ?? 0}% on closed` : 'select a chain'}
          valueClass={(displayPnl ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}
        />
        <KpiCard
          label="Unrealized"
          value={singleChain
            ? `${(displayUnrealized ?? 0) >= 0 ? '+' : ''}${formatNum(displayUnrealized, 3)} ${singleSym}`
            : '—'}
          sub={`${periodStats?.active ?? 0} open positions`}
          valueClass={(displayUnrealized ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}
        />
        <KpiCard label="Open Book" value={periodStats?.active ?? 0} sub={singleChain ? `${formatNum(chainStats[singleChain]?.investedActive, 2)} ${singleSym} deployed` : 'across chains'} />
      </div>

      {/* Per-chain cards when no filter */}
      {!chainFilter && chains.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {chains.map((c) => {
            const s = chainStats[c] || {};
            const sym = getNativeSymbol(c);
            const pnl = s.realizedPnl || 0;
            return (
              <button
                key={c}
                onClick={() => setChainFilter(c)}
                className="text-left bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 hover:border-blue-400 dark:hover:border-blue-500 transition-colors"
              >
                <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">{CHAIN_LABELS[c] || c}</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                  {s.closed ?? 0} <span className="text-sm font-normal text-gray-400">closed</span>
                </p>
                <p className={`text-sm font-semibold ${pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {pnl >= 0 ? '+' : ''}{formatNum(pnl, 3)} {sym}
                  <span className="text-gray-400 font-normal ml-1">({s.winRate ?? 0}% WR)</span>
                </p>
                <p className="text-[10px] text-gray-400 mt-1">{s.opened ?? 0} opened · {s.active ?? 0} open</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Daily P&L bars */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Daily Realized P&L</h3>
          <p className="text-[10px] text-gray-400 mb-3">P&L attributed to close date</p>
          {dailyChartData.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-12">No data for this period</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={dailyChartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} width={48} />
                <Tooltip content={<DailyTooltip />} />
                <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="3 3" />
                {activeChains.map((c) => (
                  <Bar key={c} dataKey={c} name={CHAIN_LABELS[c] || c} stackId="pnl" fill={CHAIN_COLORS[c] || '#6b7280'} radius={[2, 2, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Cumulative equity curve */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Cumulative P&L</h3>
          <p className="text-[10px] text-gray-400 mb-3">Running total per closed trade</p>
          {cumulativeChartData.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-12">No closed trades yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={cumulativeChartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                <XAxis dataKey="index" tick={{ fontSize: 10, fill: '#9ca3af' }} label={{ value: 'Trade #', position: 'insideBottom', offset: -2, fontSize: 9, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} width={48} />
                <Tooltip content={<PnlTooltip />} />
                <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="3 3" />
                {activeChains.map((c) => (
                  <Line
                    key={c}
                    type="monotone"
                    dataKey={c}
                    name={CHAIN_LABELS[c] || c}
                    stroke={CHAIN_COLORS[c] || '#6b7280'}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                ))}
                {activeChains.length > 1 && <Legend wrapperStyle={{ fontSize: 10 }} />}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Strategy + recent trades */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Strategy Breakdown</h3>
          {strategyData.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-6">No closed trades in this period</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(120, strategyData.length * 36)}>
              <BarChart data={strategyData} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#9ca3af' }} width={72} />
                <Tooltip
                  formatter={(val, name) => [name === 'closed' ? val : `${val}%`, name === 'closed' ? 'Trades' : 'Win Rate']}
                  contentStyle={{ background: '#111827', border: '1px solid #374151', borderRadius: 8, fontSize: 11 }}
                />
                <Bar dataKey="closed" name="Trades" radius={[0, 4, 4, 0]}>
                  {strategyData.map((entry, i) => (
                    <Cell key={i} fill={entry.winRate >= 50 ? '#22c55e' : entry.winRate >= 35 ? '#f97316' : '#ef4444'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
            Recent Closes
            <span className="text-xs font-normal text-gray-400 ml-2">{PERIODS.find((p) => p.id === period)?.label}</span>
          </h3>
          {recentTrades.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-6">No closes in this period</p>
          ) : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {recentTrades.map((t) => {
                const sym = getNativeSymbol(t.chain);
                const pnl = t.totalPnl || 0;
                return (
                  <div key={t._id} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-100 dark:border-gray-700 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white">{t.tokenSymbol}</span>
                      <span className="text-gray-400">{CHAIN_LABELS[t.chain]}</span>
                      <span className={`px-1 py-0.5 rounded text-[9px] ${t.isLive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
                        {t.isLive ? 'LIVE' : 'DRY'}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className={`font-semibold ${pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {pnl >= 0 ? '+' : ''}{formatNum(pnl, 3)} {sym}
                      </span>
                      <span className="text-gray-400 ml-1">({t.totalPnlPercent >= 0 ? '+' : ''}{t.totalPnlPercent}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
